import { amplifyProxy } from '@/services/directApi'

export default {
  namespaced: true,
  state: {
    landlordsBanklessRecords: [],
    landlordsEmaillessRecords: [],
    landlordsOwingRecords: [],
    landlordsTenPercentBalanceDifference: [],
    allLandlordRecords: [],
    totalCount: {
      all: 0,
      bankless: 0,
      emailless: 0,
      owing: 0,
      tenPercent: 0
    },
    pagination: {
      all: { offset: 0, limit: 10 },
      bankless: { offset: 0, limit: 10 },
      emailless: { offset: 0, limit: 10 },
      owing: { offset: 0, limit: 10 },
      tenPercent: { offset: 0, limit: 10 }
    },
    loading: {
      all: false,
      bankless: false,
      emailless: false,
      owing: false,
      tenPercent: false
    },
    error: {
      all: null,
      bankless: null,
      emailless: null,
      owing: null,
      tenPercent: null
    },
    // Page cache to store previously fetched pages
    pageCache: {
      all: {},
      bankless: {},
      emailless: {},
      owing: {},
      tenPercent: {}
    }
  },
  mutations: {
    SET_LANDLORDS_BANKLESS_RECORDS(state, data) {
      state.landlordsBanklessRecords = data
    },
    SET_LANDLORDS_EMAILLESS_RECORDS(state, data) {
      state.landlordsEmaillessRecords = data
    },
    SET_LANDLORDS_OWING_RECORDS(state, data) {
      state.landlordsOwingRecords = data
    },
    SET_LANDLORDS_TEN_PERCENT_DIFFERENCE_RECORDS(state, data) {
      state.landlordsTenPercentBalanceDifference = data
    },
    SET_ALL_LANDLORDS_RECORDS(state, data) {
      state.allLandlordRecords = data
    },
    SET_TOTAL_COUNT(state, { type, count }) {
      state.totalCount[type] = count
    },
    SET_PAGINATION(state, { type, offset, limit }) {
      state.pagination[type] = { offset, limit }
    },
    SET_LOADING(state, { type, value }) {
      state.loading[type] = value
    },
    SET_ERROR(state, { type, error }) {
      state.error[type] = error
    },
    SET_PAGE_CACHE(state, { type, key, data }) {
      state.pageCache[type][key] = data
    },
    CLEAR_PAGE_CACHE(state, type = null) {
      if (type) {
        state.pageCache[type] = {}
      } else {
        state.pageCache = {
          all: {},
          bankless: {},
          emailless: {},
          owing: {},
          tenPercent: {}
        }
      }
    }
  },
  actions: {
    async getAllLandlordsRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, landlordId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${landlordId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.all[cacheKey]) {
        const cachedData = state.pageCache.all[cacheKey]
        commit('SET_ALL_LANDLORDS_RECORDS', cachedData.Data || [])
        commit('SET_TOTAL_COUNT', { type: 'all', count: cachedData.Count || 0 })
        commit('SET_PAGINATION', { type: 'all', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'all', value: true })
      commit('SET_ERROR', { type: 'all', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/landlords/all/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(landlordId && { landlordId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'all', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || []
        const count = response.data?.Count || 0
        
        commit('SET_ALL_LANDLORDS_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'all', count })
        commit('SET_PAGINATION', { type: 'all', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'all', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'all', value: false })
      }
    },

    async getLandlordsBanklessRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, landlordId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${landlordId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.bankless[cacheKey]) {
        const cachedData = state.pageCache.bankless[cacheKey]
        commit('SET_LANDLORDS_BANKLESS_RECORDS', cachedData.Data || [])
        commit('SET_TOTAL_COUNT', { type: 'bankless', count: cachedData.Count || 0 })
        commit('SET_PAGINATION', { type: 'bankless', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'bankless', value: true })
      commit('SET_ERROR', { type: 'bankless', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/landlords/bankless/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(landlordId && { landlordId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'bankless', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || []
        const count = response.data?.Count || 0
        
        commit('SET_LANDLORDS_BANKLESS_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'bankless', count })
        commit('SET_PAGINATION', { type: 'bankless', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'bankless', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'bankless', value: false })
      }
    },

    async getLandlordsEmaillessRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, landlordId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${landlordId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.emailless[cacheKey]) {
        const cachedData = state.pageCache.emailless[cacheKey]
        commit('SET_LANDLORDS_EMAILLESS_RECORDS', cachedData.Data || [])
        commit('SET_TOTAL_COUNT', { type: 'emailless', count: cachedData.Count || 0 })
        commit('SET_PAGINATION', { type: 'emailless', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'emailless', value: true })
      commit('SET_ERROR', { type: 'emailless', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/landlords/emailless/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(landlordId && { landlordId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'emailless', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || []
        const count = response.data?.Count || 0
        
        commit('SET_LANDLORDS_EMAILLESS_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'emailless', count })
        commit('SET_PAGINATION', { type: 'emailless', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'emailless', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'emailless', value: false })
      }
    },

    async getLandlordsOwingRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, landlordId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${landlordId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.owing[cacheKey]) {
        const cachedData = state.pageCache.owing[cacheKey]
        commit('SET_LANDLORDS_OWING_RECORDS', cachedData.Data || [])
        commit('SET_TOTAL_COUNT', { type: 'owing', count: cachedData.Count || 0 })
        commit('SET_PAGINATION', { type: 'owing', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'owing', value: true })
      commit('SET_ERROR', { type: 'owing', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/landlords/owing/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(landlordId && { landlordId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'owing', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || []
        const count = response.data?.Count || 0
        
        commit('SET_LANDLORDS_OWING_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'owing', count })
        commit('SET_PAGINATION', { type: 'owing', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'owing', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'owing', value: false })
      }
    },

    async getLandlordsTenPercentDifferenceRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, landlordId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${landlordId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.tenPercent[cacheKey]) {
        const cachedData = state.pageCache.tenPercent[cacheKey]
        commit('SET_LANDLORDS_TEN_PERCENT_DIFFERENCE_RECORDS', cachedData.Data || [])
        commit('SET_TOTAL_COUNT', { type: 'tenPercent', count: cachedData.Count || 0 })
        commit('SET_PAGINATION', { type: 'tenPercent', offset, limit })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'tenPercent', value: true })
      commit('SET_ERROR', { type: 'tenPercent', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/landlords/tenPercentBalanceDifference/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(landlordId && { landlordId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'tenPercent', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data?.Data || []
        const count = response.data?.Count || 0
        
        commit('SET_LANDLORDS_TEN_PERCENT_DIFFERENCE_RECORDS', data)
        commit('SET_TOTAL_COUNT', { type: 'tenPercent', count })
        commit('SET_PAGINATION', { type: 'tenPercent', offset, limit })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'tenPercent', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'tenPercent', value: false })
      }
    }
  }
}